import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  ButtonGroup,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Phone as PhoneIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';

const SupportAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [viewType, setViewType] = useState('overview');

  // Mock analytics data
  const kpiData = [
    { 
      title: 'মোট টিকেট', 
      value: '১,২৪৫', 
      change: '+১২%', 
      trend: 'up', 
      color: '#5e35b1',
      icon: <QuestionAnswerIcon />
    },
    { 
      title: 'সমাধানকৃত টিকেট', 
      value: '১,১২৮', 
      change: '+১৮%', 
      trend: 'up', 
      color: '#2e7d32',
      icon: <CheckCircleIcon />
    },
    { 
      title: 'গড় রেসপন্স টাইম', 
      value: '২.৩ মি', 
      change: '-০.৫মি', 
      trend: 'up', 
      color: '#1976d2',
      icon: <ScheduleIcon />
    },
    { 
      title: 'গ্রাহক সন্তুষ্টি', 
      value: '৪.৬/৫', 
      change: '+০.২', 
      trend: 'up', 
      color: '#f57c00',
      icon: <StarIcon />
    },
    { 
      title: 'সক্রিয় এজেন্ট', 
      value: '২৪', 
      change: '+২', 
      trend: 'up', 
      color: '#7b1fa2',
      icon: <PersonIcon />
    },
    { 
      title: 'মোট কল', 
      value: '৮৯৬', 
      change: '+৫%', 
      trend: 'up', 
      color: '#00695c',
      icon: <PhoneIcon />
    }
  ];

  const agentPerformance = [
    { 
      name: 'রহিম উদ্দিন', 
      id: 'SA001', 
      ticketsResolved: 156, 
      avgResponseTime: '1.8 মি', 
      satisfaction: 4.8, 
      status: 'অনলাইন',
      efficiency: 94
    },
    { 
      name: 'ফাতেমা খাতুন', 
      id: 'SA002', 
      ticketsResolved: 142, 
      avgResponseTime: '2.1 মি', 
      satisfaction: 4.7, 
      status: 'অনলাইন',
      efficiency: 91
    },
    { 
      name: 'করিম মিয়া', 
      id: 'SA003', 
      ticketsResolved: 138, 
      avgResponseTime: '2.4 মি', 
      satisfaction: 4.6, 
      status: 'ব্যস্ত',
      efficiency: 88
    },
    { 
      name: 'সালমা বেগম', 
      id: 'SA004', 
      ticketsResolved: 134, 
      avgResponseTime: '2.0 মি', 
      satisfaction: 4.9, 
      status: 'অফলাইন',
      efficiency: 96
    },
    { 
      name: 'আব্দুল হক', 
      id: 'SA005', 
      ticketsResolved: 129, 
      avgResponseTime: '2.6 মি', 
      satisfaction: 4.5, 
      status: 'অনলাইন',
      efficiency: 85
    }
  ];

  const issueCategories = [
    { category: 'অর্ডার সম্পর্কিত', count: 456, percentage: 37, color: '#5e35b1' },
    { category: 'ডেলিভারি সমস্যা', count: 298, percentage: 24, color: '#1976d2' },
    { category: 'পেমেন্ট ইস্যু', count: 187, percentage: 15, color: '#f57c00' },
    { category: 'প্রোডাক্ট সমস্যা', count: 156, percentage: 13, color: '#2e7d32' },
    { category: 'অ্যাকাউন্ট সমস্যা', count: 89, percentage: 7, color: '#d32f2f' },
    { category: 'অন্যান্য', count: 59, percentage: 5, color: '#7b1fa2' }
  ];

  const hourlyData = [
    { hour: '০৯:০০', tickets: 45, calls: 38 },
    { hour: '১০:০০', tickets: 67, calls: 52 },
    { hour: '১১:০০', tickets: 89, calls: 71 },
    { hour: '১২:০০', tickets: 72, calls: 58 },
    { hour: '১৩:০০', tickets: 56, calls: 43 },
    { hour: '১৪:০০', tickets: 78, calls: 65 },
    { hour: '১৫:০০', tickets: 94, calls: 78 },
    { hour: '১৬:০০', tickets: 82, calls: 69 },
    { hour: '১৭:০০', tickets: 59, calls: 47 }
  ];

  const satisfactionTrend = [
    { period: 'জানুয়ারি', score: 4.2 },
    { period: 'ফেব্রুয়ারি', score: 4.3 },
    { period: 'মার্চ', score: 4.4 },
    { period: 'এপ্রিল', score: 4.5 },
    { period: 'মে', score: 4.4 },
    { period: 'জুন', score: 4.6 },
    { period: 'জুলাই', score: 4.6 }
  ];

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'অনলাইন': return 'success';
      case 'ব্যস্ত': return 'warning';
      case 'অফলাইন': return 'error';
      default: return 'default';
    }
  };

  const exportData = () => {
    alert('ডেটা এক্সপোর্ট হচ্ছে...');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            সাপোর্ট বিশ্লেষণ
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            পারফরম্যান্স মেট্রিক্স এবং ট্রেন্ড বিশ্লেষণ
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>সময়কাল</InputLabel>
            <Select
              value={timeRange}
              label="সময়কাল"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="1day">আজ</MenuItem>
              <MenuItem value="7days">৭ দিন</MenuItem>
              <MenuItem value="30days">৩০ দিন</MenuItem>
              <MenuItem value="90days">৯০ দিন</MenuItem>
            </Select>
          </FormControl>
          
          <ButtonGroup size="small">
            <Button 
              variant={viewType === 'overview' ? 'contained' : 'outlined'}
              onClick={() => setViewType('overview')}
            >
              সারসংক্ষেপ
            </Button>
            <Button 
              variant={viewType === 'detailed' ? 'contained' : 'outlined'}
              onClick={() => setViewType('detailed')}
            >
              বিস্তারিত
            </Button>
          </ButtonGroup>
          
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={exportData}
          >
            এক্সপোর্ট
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          মূল পারফরম্যান্স সূচক (KPI)
        </Typography>
        <Grid container spacing={3}>
          {kpiData.map((kpi, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: kpi.color, mr: 2, width: 40, height: 40 }}>
                      {kpi.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {kpi.title}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: kpi.color }}>
                        {kpi.value}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {kpi.trend === 'up' ? (
                      <TrendingUpIcon sx={{ color: 'success.main', mr: 0.5 }} />
                    ) : (
                      <TrendingDownIcon sx={{ color: 'error.main', mr: 0.5 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      color={kpi.trend === 'up' ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 500 }}
                    >
                      {kpi.change}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Agent Performance */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              এজেন্ট পারফরম্যান্স
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>এজেন্ট</TableCell>
                    <TableCell align="center">সমাধানকৃত</TableCell>
                    <TableCell align="center">রেসপন্স টাইম</TableCell>
                    <TableCell align="center">সন্তুষ্টি</TableCell>
                    <TableCell align="center">দক্ষতা</TableCell>
                    <TableCell align="center">স্ট্যাটাস</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentPerformance.map((agent) => (
                    <TableRow key={agent.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: '#5e35b1' }}>
                            {agent.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {agent.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {agent.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{agent.ticketsResolved}</TableCell>
                      <TableCell align="center">{agent.avgResponseTime}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <StarIcon sx={{ color: '#f57c00', mr: 0.5, fontSize: '1rem' }} />
                          {agent.satisfaction}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={agent.efficiency} 
                            sx={{ width: 50, mr: 1 }}
                          />
                          {agent.efficiency}%
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={agent.status} 
                          color={getStatusColor(agent.status)} 
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Issue Categories */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              সমস্যার ধরণ বিতরণ
            </Typography>
            <Box sx={{ mt: 2 }}>
              {issueCategories.map((category, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {category.category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.count} ({category.percentage}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={category.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: '#f5f5f5',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: category.color,
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Hourly Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              ঘণ্টাভিত্তিক কার্যক্রম
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>সময়</TableCell>
                    <TableCell align="center">টিকেট</TableCell>
                    <TableCell align="center">কল</TableCell>
                    <TableCell align="center">মোট</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hourlyData.map((hour, index) => (
                    <TableRow key={index}>
                      <TableCell>{hour.hour}</TableCell>
                      <TableCell align="center">{hour.tickets}</TableCell>
                      <TableCell align="center">{hour.calls}</TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {hour.tickets + hour.calls}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Satisfaction Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              গ্রাহক সন্তুষ্টির ট্রেন্ড
            </Typography>
            <Box sx={{ mt: 2 }}>
              {satisfactionTrend.map((month, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {month.period}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <StarIcon sx={{ color: '#f57c00', mr: 0.5, fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {month.score}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(month.score / 5) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      backgroundColor: '#f5f5f5',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#f57c00',
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              দ্রুত পরিসংখ্যান
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    ৯২%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    প্রথম যোগাযোগে সমাধান
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                    ৪.৬
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    গড় রেটিং (৫ এর মধ্যে)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                    ৮৭%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SLA কমপ্লায়েন্স
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                    ১৫ সে
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    গড় অপেক্ষার সময়
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupportAnalytics; 